import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, aiProviderApi, billingApi, getAiProviderLabel, getAiProviderModel, setUnauthorizedHandler, AuthUser } from '@/services/api';
import { useAppStore } from '@/stores/appStore';

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (user: AuthUser) => void;
  updateUser: (partial: Partial<AuthUser>) => void;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  sessionExpired: boolean;
  dismissSessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const setAiProvider = useAppStore((s) => s.setAiProvider);
  const setAiUsage = useAppStore((s) => s.setAiUsage);

  const dismissSessionExpired = () => setSessionExpired(false);

  // Registered globally so apiRequest() can surface a "please sign in again" prompt on any
  // authenticated request that comes back 401 (token expired or invalidated mid-session).
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      setSessionExpired(true);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authApi.getMe();
      setUser(response.user);
      // Load AI provider config so all intelligence components know which provider is active
      aiProviderApi.get().then((cfg) => {
        setAiProvider(getAiProviderLabel(cfg), getAiProviderModel(cfg));
      }).catch(() => {/* non-fatal */});
      // Load AI usage so credit checks work throughout the app
      billingApi.getUsage().then(setAiUsage).catch(() => {/* non-fatal */});
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = (user: AuthUser) => {
    // Clear any prior user's saved-view session state so it can't leak across accounts
    useAppStore.getState().resetViews();
    setUser(user);
  };

  // Merge a partial update into the current user (e.g. after pinning a favorite view)
  const updateUser = (partial: Partial<AuthUser>) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      useAppStore.getState().resetViews();
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    updateUser,
    logout,
    checkAuth,
    sessionExpired,
    dismissSessionExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hooks for role checking
export function useIsAdmin() {
  const { user } = useAuth();
  return user?.role === 'ADMIN';
}

export function useIsEditor() {
  const { user } = useAuth();
  return user?.role === 'EDITOR' || user?.role === 'ADMIN';
}

export function useCanEdit(initiativeUserId?: string) {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === 'ADMIN') return true;
  if (user.role === 'EDITOR') {
    return !initiativeUserId || initiativeUserId === user.id;
  }
  return false;
}
